// Die Pausenregel einstellen — hier legt Sebastian fest, was abgezogen wird.
//
// Vorbelegt mit dem Arbeitszeitgesetz (§4 ArbZG): ab mehr als 6 Stunden 30
// Minuten, ab mehr als 9 Stunden 45 Minuten. Der Hof darf grosszügiger sein,
// aber wer darunter geht, bekommt es gesagt — still davonlaufen lassen wäre bei
// einer Prüfung teuer.

import { useEffect, useState } from "react";
import { usePausenregel, useSpeicherePausenregel, GESETZ, type Pausenstufe } from "@/lib/pausenregel";
import { Button, Card, Input, cn } from "@/design-system/ui";

const stunden = (min: number) => (min / 60).toLocaleString("de-DE", { maximumFractionDigits: 2 });

export function PausenregelKarte() {
  const { data: gespeichert } = usePausenregel();
  const speichern = useSpeicherePausenregel();
  const [stufen, setStufen] = useState<Pausenstufe[]>(GESETZ);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gesichert, setGesichert] = useState(false);

  useEffect(() => {
    if (gespeichert) setStufen(gespeichert);
  }, [gespeichert]);

  const setzen = (i: number, feld: keyof Pausenstufe, wert: string) => {
    setGesichert(false);
    const n = Number(wert.replace(",", "."));
    setStufen((s) => s.map((x, k) => (k === i ? { ...x, [feld]: Number.isFinite(n) ? n : 0 } : x)));
  };

  // Unter dem Gesetz? Das sagen wir, aber wir verbieten es nicht: Vielleicht
  // gibt es Gründe, die die App nicht kennt. Schweigen wäre falsch, Bevormunden
  // auch.
  const unterGesetz = GESETZ.some((g) =>
    stufen.every((s) => !(s.ab_minuten <= g.ab_minuten && s.pause_minuten >= g.pause_minuten))
  );

  return (
    <Card className="p-4">
      <h3 className="text-[15px] font-semibold text-ink-50">Pausenabzug</h3>
      <p className="mt-1 text-sm text-ink-400">
        Wird beim Ausstempeln automatisch von der Arbeitszeit abgezogen. Die
        Mitarbeiter tragen nichts mehr selbst ein.
      </p>

      <ul className="mt-4 space-y-2">
        {stufen.map((s, i) => (
          <li key={i} className="flex items-center gap-2">
            <span className="text-sm text-ink-400">ab</span>
            <div className="w-20">
              <Input
                value={String(s.ab_minuten)}
                onChange={(e) => setzen(i, "ab_minuten", e.target.value)}
                inputMode="numeric"
                className="tabular-nums"
              />
            </div>
            <span className="w-24 text-sm text-ink-500">min ({stunden(s.ab_minuten)} h)</span>
            <span className="text-sm text-ink-400">→</span>
            <div className="w-20">
              <Input
                value={String(s.pause_minuten)}
                onChange={(e) => setzen(i, "pause_minuten", e.target.value)}
                inputMode="numeric"
                className="tabular-nums"
              />
            </div>
            <span className="text-sm text-ink-500">min Pause</span>
            <button
              onClick={() => {
                setGesichert(false);
                setStufen((x) => x.filter((_, k) => k !== i));
              }}
              aria-label="Stufe entfernen"
              className="ml-auto h-8 w-8 shrink-0 rounded-lg text-ink-500 transition-colors duration-fast hover:bg-ink-800 hover:text-danger-fg"
            >
              ✕
            </button>
          </li>
        ))}
      </ul>

      {stufen.length === 0 && (
        <p className="mt-3 rounded-lg border border-warn-bg/30 bg-warn-bg/10 px-3 py-2 text-sm text-warn-fg">
          Ohne Stufe wird gar keine Pause abgezogen — die volle Anwesenheit zählt
          dann als Arbeitszeit.
        </p>
      )}

      {unterGesetz && stufen.length > 0 && (
        <p className="mt-3 rounded-lg border border-warn-bg/30 bg-warn-bg/10 px-3 py-2 text-sm text-warn-fg">
          Weniger als das Arbeitszeitgesetz vorsieht (30 min ab 6 h, 45 min ab 9 h).
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          onClick={() => {
            setGesichert(false);
            setStufen((s) => [...s, { ab_minuten: 360, pause_minuten: 30 }]);
          }}
        >
          + Stufe
        </Button>
        <Button
          size="sm"
          onClick={() => {
            setGesichert(false);
            setStufen(GESETZ);
          }}
        >
          Gesetz einsetzen
        </Button>

        <Button
          variant="primary"
          size="sm"
          className="ml-auto"
          loading={speichern.isPending}
          onClick={async () => {
            setFehler(null);
            try {
              await speichern.mutateAsync(stufen);
              setGesichert(true);
            } catch (e) {
              setFehler((e as Error).message || "Speichern fehlgeschlagen.");
            }
          }}
        >
          Speichern
        </Button>
      </div>

      {gesichert && <p className="mt-2 text-sm text-gruen-400">Gespeichert.</p>}
      {fehler && (
        <p className="mt-2 rounded-lg border border-danger-bg/30 bg-danger-bg/10 px-3 py-2 text-sm text-danger-fg">
          {fehler}
        </p>
      )}

      {/* Was das konkret heisst — eine Regel, die man nachrechnen muss, wird
          falsch verstanden. */}
      <p className={cn("mt-3 text-xs text-ink-500")}>
        Beispiel: 8 Stunden anwesend →{" "}
        {stufen.reduce((p, s) => (480 > s.ab_minuten && s.pause_minuten > p ? s.pause_minuten : p), 0)} min
        Pause. Bereits gebuchte Schichten bleiben unverändert.
      </p>
    </Card>
  );
}
